import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, Loader2, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

/**
 * 2FA verification page — shown after password login when TOTP is enabled.
 * Receives userId via location state.
 */
export default function TwoFactorVerify() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  const userId = location.state?.user_id;

  useEffect(() => {
    if (!userId) {
      navigate('/admin/login');
      return;
    }
    inputRef.current?.focus();
  }, [userId, navigate]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (code.length !== 6) { toast.error('Enter the 6-digit code from your authenticator app'); return; }

    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/2fa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ user_id: userId, code })
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Verification failed');

      login(data.access_token, data.user);
      toast.success('Verified! Welcome back.');
      navigate('/admin');
    } catch (e) {
      toast.error(e.message);
      setCode('');
      inputRef.current?.focus();
    } finally {
      setSubmitting(false);
    }
  };

  // Auto-submit when 6 digits entered
  const handleCodeChange = (val) => {
    const digits = val.replace(/\D/g, '').slice(0, 6);
    setCode(digits);
    if (digits.length === 6) {
      setTimeout(() => handleSubmit(), 100);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4" data-testid="2fa-verify-page">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-sm bg-white rounded-3xl shadow-2xl p-8"
      >
        {/* Icon */}
        <div className="text-center mb-7">
          <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="h-8 w-8 text-amber-600" />
          </div>
          <h1 className="text-2xl font-black text-gray-900">Two-Factor Auth</h1>
          <p className="text-gray-500 text-sm mt-1">Enter the 6-digit code from your Google Authenticator app</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <input
              ref={inputRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={code}
              onChange={e => handleCodeChange(e.target.value)}
              placeholder="000000"
              maxLength={6}
              autoComplete="one-time-code"
              className="w-full h-16 text-4xl font-mono font-bold text-center tracking-[0.5em] border-2 border-gray-200 focus:border-amber-500 rounded-2xl outline-none transition-colors"
              data-testid="totp-code-input"
            />
          </div>

          <button
            type="submit"
            disabled={submitting || code.length !== 6}
            className="w-full h-14 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-2xl text-base flex items-center justify-center gap-2 disabled:opacity-50 transition-all"
            data-testid="verify-totp-btn"
          >
            {submitting ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <><ShieldCheck className="h-5 w-5" />Verify &amp; Sign In</>
            )}
          </button>
        </form>

        <button
          onClick={() => navigate('/admin/login')}
          className="mt-4 w-full flex items-center justify-center gap-1 text-sm text-gray-400 hover:text-gray-600"
        >
          <ArrowLeft className="h-4 w-4" /> Back to login
        </button>
      </motion.div>
    </div>
  );
}
